import {
  ArrowCounterClockwiseIcon,
  ArrowsClockwiseIcon,
  PencilSimpleIcon,
  PlusIcon,
  SignOutIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { AllocateMachineDialog } from '@/components/machines/AllocateMachineDialog';
import { AllocationHistoryTable } from '@/components/machines/AllocationHistoryTable';
import { DeallocateMachineDialog } from '@/components/machines/DeallocateMachineDialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { formatMachineName, vehicleNumberFromCustomFields } from '@/lib/utils/machine-label';
import { getMachineTypeRequest } from '@/services/machine-types';
import {
  deactivateMachineRequest,
  getMachineAllocationsRequest,
  getMachineRequest,
  reactivateMachineRequest,
} from '@/services/machines';
import type { MachineTypeDetail } from '@/types/machine-types';
import type { MachineAllocation, MachineDetail } from '@/types/machines';

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatCustomFieldValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return JSON.stringify(value);
}

export function MachineDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [allocateOpen, setAllocateOpen] = useState<boolean>(false);
  const [deallocateOpen, setDeallocateOpen] = useState<boolean>(false);
  const [deactivateOpen, setDeactivateOpen] = useState<boolean>(false);

  const detailQuery = useQuery<MachineDetail, ApiError>({
    queryKey: ['machine', id],
    queryFn: () => getMachineRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 5 * 60_000,
  });

  const allocationsQuery = useQuery<readonly MachineAllocation[], ApiError>({
    queryKey: ['machine-allocations', id],
    queryFn: () => getMachineAllocationsRequest(id),
    enabled: Number.isFinite(id) && id > 0,
    staleTime: 30_000,
  });

  const machineTypeId = detailQuery.data?.machineType.id ?? 0;
  const machineTypeQuery = useQuery<MachineTypeDetail, ApiError>({
    queryKey: ['machine-type', machineTypeId],
    queryFn: () => getMachineTypeRequest(machineTypeId),
    enabled: machineTypeId > 0,
    staleTime: 5 * 60_000,
  });

  const deactivateMutation = useMutation<undefined, ApiError>({
    mutationFn: async () => {
      await deactivateMachineRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Machine deactivated');
      void queryClient.invalidateQueries({ queryKey: ['machines'] });
      void queryClient.invalidateQueries({ queryKey: ['machine', id] });
      void queryClient.invalidateQueries({ queryKey: ['machine-allocations', id] });
      setDeactivateOpen(false);
      void navigate('/machines');
    },
    onError: (err) => {
      toast.error('Could not deactivate', { description: err.detail ?? err.message });
    },
  });

  const reactivateMutation = useMutation<undefined, ApiError>({
    mutationFn: async () => {
      await reactivateMachineRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Machine reactivated');
      void queryClient.invalidateQueries({ queryKey: ['machines'] });
      void queryClient.invalidateQueries({ queryKey: ['machine', id] });
    },
    onError: (err) => {
      toast.error('Could not reactivate', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('machines.read')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/machines" replace />;
  }

  const canEdit = permissions.includes('machines.update');
  const canDeactivate = permissions.includes('machines.delete');
  const machine = detailQuery.data;
  const schema = machineTypeQuery.data?.customFields ?? [];

  function refetchMachineAndAllocations(): void {
    void queryClient.invalidateQueries({ queryKey: ['machines'] });
    void queryClient.invalidateQueries({ queryKey: ['machine', id] });
    void queryClient.invalidateQueries({ queryKey: ['machine-allocations', id] });
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {machine
              ? formatMachineName(
                  machine.name,
                  vehicleNumberFromCustomFields(machine.customFieldValues),
                )
              : 'Machine'}{' '}
            {machine && (
              <span
                className="ml-2 font-mono text-sm text-muted-foreground"
                data-testid="machine-detail-code"
              >
                {machine.code}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">
            {machine?.machineType.name ?? 'Profile, custom fields, and allocation history.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {machine && (
            <Badge
              variant={machine.isActive ? 'default' : 'secondary'}
              data-testid="machine-detail-status"
            >
              {machine.isActive ? 'Active' : 'Inactive'}
            </Badge>
          )}
          {machine !== undefined && canEdit && machine.isActive && (
            <Button asChild data-testid="machine-detail-edit">
              <Link to={`/machines/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {machine !== undefined && canEdit && machine.isActive && machine.currentSite === null && (
            <Button
              type="button"
              onClick={() => {
                setAllocateOpen(true);
              }}
              data-testid="machine-detail-allocate"
            >
              <PlusIcon size={16} weight="bold" /> Allocate to Site
            </Button>
          )}
          {machine !== undefined && canEdit && machine.isActive && machine.currentSite !== null && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setAllocateOpen(true);
                }}
                data-testid="machine-detail-reallocate"
              >
                <ArrowsClockwiseIcon size={16} weight="bold" /> Reallocate
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDeallocateOpen(true);
                }}
                data-testid="machine-detail-deallocate"
              >
                <SignOutIcon size={16} weight="bold" /> Deallocate
              </Button>
            </>
          )}
          {machine !== undefined && canDeactivate && machine.isActive && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeactivateOpen(true);
              }}
              data-testid="machine-detail-deactivate"
            >
              <TrashIcon size={16} weight="bold" /> Deactivate
            </Button>
          )}
          {machine !== undefined && canEdit && !machine.isActive && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reactivateMutation.mutate();
              }}
              disabled={reactivateMutation.isPending}
              data-testid="machine-detail-reactivate"
            >
              <ArrowCounterClockwiseIcon size={16} weight="bold" />{' '}
              {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
            </Button>
          )}
        </div>
      </div>

      {detailQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="machine-detail-loading">
          Loading machine…
        </p>
      )}
      {detailQuery.isError && (
        <p className="text-sm text-destructive" data-testid="machine-detail-error">
          Failed to load machine.
        </p>
      )}

      {machine && (
        <Tabs defaultValue="profile" className="space-y-4">
          <div className="overflow-x-auto">
          <TabsList data-testid="machine-detail-tabs">
            <TabsTrigger value="profile" data-testid="machine-detail-tab-profile">
              Profile
            </TabsTrigger>
            <TabsTrigger value="customfields" data-testid="machine-detail-tab-customfields">
              Custom Fields
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="machine-detail-tab-history">
              Allocation History
            </TabsTrigger>
            <TabsTrigger value="logsheets" data-testid="machine-detail-tab-logsheets">
              Logsheet History
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle>Profile</CardTitle>
                <CardDescription>Read-only view. Use Edit to make changes.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div>
                  <div className="text-muted-foreground text-xs">Name</div>
                  <div>{machine.name}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Code</div>
                  <div className="font-mono">{machine.code}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Machine type</div>
                  <div data-testid="machine-detail-machinetype">
                    <Link
                      to={`/machine-types/${String(machine.machineType.id)}`}
                      className="text-primary hover:underline"
                    >
                      {machine.machineType.name}
                    </Link>
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Current site</div>
                  <div data-testid="machine-detail-currentsite">
                    {machine.currentSite === null ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <Link
                        to={`/sites/${String(machine.currentSite.id)}`}
                        className="text-primary hover:underline"
                      >
                        {machine.currentSite.name} ({machine.currentSite.code})
                      </Link>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Created</div>
                  <div className="font-mono text-xs">{formatDateTime(machine.createdAt)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs">Updated</div>
                  <div className="font-mono text-xs">{formatDateTime(machine.updatedAt)}</div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customfields">
            <Card>
              <CardHeader>
                <CardTitle>Custom fields</CardTitle>
                <CardDescription>
                  Read-only view per the persisted machine-type schema. Use Edit to update values.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {schema.length === 0 ? (
                  <Alert data-testid="machine-detail-customfields-empty">
                    <AlertTitle>No custom fields</AlertTitle>
                    <AlertDescription>
                      This machine type has no custom fields defined.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="overflow-x-auto">
                  <Table data-testid="machine-detail-customfields-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Label</TableHead>
                        <TableHead>Key</TableHead>
                        <TableHead>Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schema.map((field) => (
                        <TableRow
                          key={field.key}
                          data-testid={`machine-detail-customfield-row-${field.key}`}
                        >
                          <TableCell>{field.label}</TableCell>
                          <TableCell className="font-mono text-xs">{field.key}</TableCell>
                          <TableCell>
                            {formatCustomFieldValue(machine.customFieldValues[field.key])}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Allocation history</CardTitle>
                <CardDescription>
                  Reverse-chronological — every allocation row from creation to today.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {allocationsQuery.isLoading && (
                  <p
                    className="text-sm text-muted-foreground"
                    data-testid="machine-detail-history-loading"
                  >
                    Loading allocation history…
                  </p>
                )}
                {allocationsQuery.isError && (
                  <p
                    className="text-sm text-destructive"
                    data-testid="machine-detail-history-error"
                  >
                    Failed to load allocation history.
                  </p>
                )}
                {allocationsQuery.data && <AllocationHistoryTable rows={allocationsQuery.data} />}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logsheets">
            <Card>
              <CardHeader>
                <CardTitle>Logsheet history</CardTitle>
                <CardDescription>Phase 12 will populate this tab.</CardDescription>
              </CardHeader>
              <CardContent>
                <p
                  className="text-sm text-muted-foreground"
                  data-testid="machine-detail-logsheets-placeholder"
                >
                  Logsheets are coming in Phase 12.
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {machine && (
        <AllocateMachineDialog
          open={allocateOpen}
          onOpenChange={setAllocateOpen}
          machineId={id}
          currentSiteId={machine.currentSite?.id ?? null}
          onSuccess={() => {
            refetchMachineAndAllocations();
            toast.success('Machine allocated');
          }}
        />
      )}

      {machine?.currentSite != null && (
        <DeallocateMachineDialog
          open={deallocateOpen}
          onOpenChange={setDeallocateOpen}
          machineId={id}
          currentSiteName={machine.currentSite.name}
          onSuccess={() => {
            refetchMachineAndAllocations();
            toast.success('Machine deallocated');
          }}
        />
      )}

      {machine && (
        <AlertDialog
          open={deactivateOpen}
          onOpenChange={(open) => {
            setDeactivateOpen(open);
          }}
        >
          <AlertDialogContent data-testid="machine-detail-deactivate-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate {machine.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                The machine will be marked inactive and any open allocation row will close. You can
                reactivate later.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deactivateMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deactivateMutation.mutate();
                }}
                disabled={deactivateMutation.isPending}
                data-testid="machine-detail-deactivate-confirm-button"
              >
                {deactivateMutation.isPending ? 'Deactivating…' : 'Deactivate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
